import { isTouch, onTouchChange } from '../core/device';
import type { Input } from '../core/Input';
import { h } from './dom';
import { setRich } from '../icons/dom';

const STICK_RADIUS = 50;

/** Pointer capture keeps a drag alive when the finger leaves the zone; it's a nicety, never fatal. */
function capture(el: HTMLElement, pointerId: number): void {
  try {
    el.setPointerCapture(pointerId);
  } catch {
    // Some browsers reject capture for pointers that already ended.
  }
}

/**
 * On-screen joystick, look/pinch area and action buttons for phones and tablets.
 * Only shown on touch devices.
 */
export class TouchControls {
  readonly el: HTMLDivElement;
  private readonly sprintBtn: HTMLButtonElement;
  private readonly beerBtn: HTMLButtonElement;
  private readonly lampBtn: HTMLButtonElement;
  private beerCode = 'KeyQ';
  private driving = false;
  private shown = true;

  constructor(
    parent: HTMLElement,
    private readonly input: Input,
  ) {
    const stickZone = this.buildStick();
    const lookZone = this.buildLook();

    this.beerBtn = this.tapButton(':beer:', 'Crack a cold one', () => this.beerCode);
    this.sprintBtn = h('button', {
      class: 'tbtn',
      text: ':croc:',
      attrs: { 'aria-label': 'Sport mode (sprint)', 'aria-pressed': 'false' },
      on: {
        pointerdown: (e) => {
          e.preventDefault();
          const on = !input.virtualHeld.has('ShiftLeft');
          if (on) input.virtualHeld.add('ShiftLeft');
          else input.virtualHeld.delete('ShiftLeft');
          this.sprintBtn.classList.toggle('on', on);
          this.sprintBtn.setAttribute('aria-pressed', String(on));
        },
      },
    });
    this.lampBtn = this.tapButton(':flashlight:', 'Flashlight', () => 'KeyL', 'mini');
    this.lampBtn.hidden = true;
    const minis = h(
      'div',
      { class: 'touch-minis' },
      this.tapButton(':eye:', 'First-person view', () => 'KeyV', 'mini'),
      this.lampBtn,
    );
    const buttons = h(
      'div',
      { class: 'touch-buttons' },
      minis,
      this.beerBtn,
      this.tapButton('E', 'Interact', () => 'KeyE', 'big'),
      this.sprintBtn,
      this.holdButton(':hop:', 'Jump / handbrake', 'Space'),
      this.tapButton(':hotdog:', 'Eat', () => 'KeyF'),
      this.tapButton(':phone:', 'Phone', () => 'KeyP'),
    );
    this.el = h('div', { class: 'touch', hidden: !isTouch() }, stickZone, lookZone, buttons);
    parent.append(this.el);
    onTouchChange((on) => (this.el.hidden = !on));
  }

  set visible(v: boolean) {
    this.shown = v;
    this.el.style.display = v ? '' : 'none';
  }

  get visible(): boolean {
    return this.shown;
  }

  /** The flashlight button shows once you own one, lit while it's on. */
  setLamp(owned: boolean, on: boolean): void {
    if (this.lampBtn.hidden === owned) this.lampBtn.hidden = !owned;
    this.lampBtn.classList.toggle('on', on);
  }

  /** While driving, the beer button becomes the horn (no drinking and driving, Bubba). */
  setDriving(driving: boolean): void {
    if (driving === this.driving) return;
    this.driving = driving;
    this.beerCode = driving ? 'KeyH' : 'KeyQ';
    setRich(this.beerBtn, driving ? ':air-horn:' : ':beer:');
    this.beerBtn.setAttribute('aria-label', driving ? 'Honk' : 'Crack a cold one');
  }

  private tapButton(label: string, aria: string, code: () => string, cls = ''): HTMLButtonElement {
    return h('button', {
      class: `tbtn ${cls}`,
      text: label,
      attrs: { 'aria-label': aria },
      on: {
        pointerdown: (e) => {
          e.preventDefault();
          this.input.press(code());
        },
      },
    });
  }

  /** A button that counts as held while touched (handbrake) and as a press (jump). */
  private holdButton(label: string, aria: string, code: string): HTMLButtonElement {
    const btn = h('button', { class: 'tbtn', text: label, attrs: { 'aria-label': aria } });
    const release = (): void => {
      this.input.virtualHeld.delete(code);
    };
    btn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.input.press(code);
      this.input.virtualHeld.add(code);
    });
    btn.addEventListener('pointerup', release);
    btn.addEventListener('pointercancel', release);
    btn.addEventListener('pointerleave', release);
    return btn;
  }

  private buildStick(): HTMLDivElement {
    const zone = h('div', { class: 'stick-zone' });
    const base = h('div', { class: 'stick-base', hidden: true });
    const knob = h('div', { class: 'stick-knob' });
    base.append(knob);
    zone.append(base);
    let id: number | null = null;
    let ox = 0;
    let oy = 0;
    zone.addEventListener('pointerdown', (e) => {
      id = e.pointerId;
      capture(zone, e.pointerId);
      const r = zone.getBoundingClientRect();
      ox = e.clientX;
      oy = e.clientY;
      base.hidden = false;
      base.style.left = `${e.clientX - r.left}px`;
      base.style.top = `${e.clientY - r.top}px`;
    });
    zone.addEventListener('pointermove', (e) => {
      if (e.pointerId !== id) return;
      let dx = e.clientX - ox;
      let dy = e.clientY - oy;
      const len = Math.hypot(dx, dy);
      if (len > STICK_RADIUS) {
        dx = (dx / len) * STICK_RADIUS;
        dy = (dy / len) * STICK_RADIUS;
      }
      knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
      this.input.stick.x = dx / STICK_RADIUS;
      this.input.stick.y = -dy / STICK_RADIUS;
    });
    const end = (e: PointerEvent): void => {
      if (e.pointerId !== id) return;
      id = null;
      base.hidden = true;
      knob.style.transform = '';
      this.input.stick.x = 0;
      this.input.stick.y = 0;
    };
    zone.addEventListener('pointerup', end);
    zone.addEventListener('pointercancel', end);
    return zone;
  }

  /** One finger drags the camera; two fingers pinch to zoom. */
  private buildLook(): HTMLDivElement {
    const zone = h('div', { class: 'look-zone' });
    const pointers = new Map<number, { x: number; y: number }>();
    let pinch = 0;
    const spread = (): number => {
      const [a, b] = [...pointers.values()];
      return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
    };
    zone.addEventListener('pointerdown', (e) => {
      capture(zone, e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2) pinch = spread();
    });
    zone.addEventListener('pointermove', (e) => {
      const p = pointers.get(e.pointerId);
      if (!p) return;
      if (pointers.size >= 2) {
        p.x = e.clientX;
        p.y = e.clientY;
        const d = spread();
        // Fingers apart = zoom in (negative wheel).
        this.input.wheel += (pinch - d) * 0.03;
        pinch = d;
        return;
      }
      this.input.addLook((e.clientX - p.x) * 1.4, (e.clientY - p.y) * 1.4);
      p.x = e.clientX;
      p.y = e.clientY;
    });
    const end = (e: PointerEvent): void => {
      pointers.delete(e.pointerId);
      pinch = spread();
    };
    zone.addEventListener('pointerup', end);
    zone.addEventListener('pointercancel', end);
    return zone;
  }
}
